import React, { FC, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import theme from '^/theme';

export enum CheckboxState {
  CHECKED = 'checked',
  UNCHECKED = 'unchecked',
  INDETERMINATE = 'indeterminate',
}
export type CheckboxStateType = (typeof CheckboxState)[keyof typeof CheckboxState];

interface CheckboxStyleProps {
  disabled?: boolean;
  state: CheckboxState;
}

export interface TriStateCheckboxProps {
  state: CheckboxState;
  disabled?: boolean;
  label?: string;
  onChange?(newState: CheckboxState): void;
  className?: string;
  id?: string;
}

const Root = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const CheckboxWrapper = styled.div<CheckboxStyleProps>(({ disabled }) => ({
  position: 'relative',
  width: '16px',
  height: '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
}));

const HiddenInput = styled.input({
  position: 'absolute',
  opacity: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  cursor: 'inherit',
});

const CheckboxVisual = styled.label<CheckboxStyleProps>(({ state, disabled }) => ({
  display: 'flex',
  width: '16px',
  height: '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  border: `2px solid ${
    disabled
      ? theme.colorTokens.disabledGray
      : state === 'unchecked'
      ? theme.colorTokens.disabledGray
      : theme.colorTokens.focusSkyblue
  }`,
  borderRadius: '4px',
  backgroundColor: state !== 'unchecked' ? theme.colorTokens.focusSkyblue : theme.colors.white[100],
  position: 'relative',

  '&::after': {
    content: "''",
    position: 'absolute',
    display: state === 'unchecked' ? 'none' : 'block',
    ...(state === 'checked'
      ? {
          left: '50%',
          top: '40%',
          width: '5px',
          height: '10px',
          border: `solid ${theme.colors.white[100]}`,
          borderWidth: '0 2px 2px 0',
          transform: 'translate(-50%, -50%) rotate(45deg)',
        }
      : state === 'indeterminate'
      ? {
          left: '50%',
          top: '50%',
          width: '8px',
          height: '2px',
          backgroundColor: theme.colors.white[100],
          transform: 'translate(-50%, -50%)',
        }
      : {}),
  },
}));

const Label = styled.label({
  fontSize: '14px',
  color: theme.colorTokens.textGrayDark,
  cursor: 'pointer',
  userSelect: 'none',
});

export const TriStateCheckbox: FC<TriStateCheckboxProps> = ({
  state,
  disabled = false,
  label,
  onChange,
  id,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = state === CheckboxState.INDETERMINATE;
    }
  }, [state]);

  const handleChange = useCallback(() => {
    if (!disabled && onChange) {
      const nextState: CheckboxState =
        state === CheckboxState.UNCHECKED ? CheckboxState.CHECKED : CheckboxState.UNCHECKED;
      onChange(nextState);
    }
  }, [state, disabled, onChange]);

  return (
    <Root className={className}>
      <CheckboxWrapper disabled={false} state={state}>
        <HiddenInput
          id={`toggle-checkbox-${id}`}
          ref={inputRef}
          type="checkbox"
          checked={state === 'checked'}
          onChange={handleChange}
          disabled={false}
        />
        <CheckboxVisual htmlFor={`toggle-checkbox-${id}`} state={state} disabled={disabled} />
      </CheckboxWrapper>
      {label && <Label onClick={disabled ? undefined : handleChange}>{label}</Label>}
    </Root>
  );
};

export default TriStateCheckbox;
